import * as reactiveUtils from "@arcgis/core/core/reactiveUtils";

import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { isWidgetDisplayed } from "../../esri/widgets/expandUtils";
import { useReports } from "../../hooks";
import useDragSidebar from "../../hooks/useDragSidebar";
import { setEditableFeature } from "../../redux/action/Dashboard-action";
import { view } from "../../utils/API";
import { ConfigContext } from "../../utils/ConfigContext";
import { getOpsColor, ROLE_EDITOR } from "../../utils/helper";
import { SectionItem } from "../FeatureTable/FeatureTable-styled";
import { FaChevronRight } from "../Icons";
import LatestUpdates, {
  LatestUpdatesIcon,
} from "../LatestUpdates/new/LatestUpdates";
import {
  StyledResizer,
  StyledResizerButton,
  StyledResizerWrapper,
  ViewerSidebar,
  ViewerSidebarBody,
} from "../LatestUpdates/new/LatestUpdates-styled";
import ReportButton from "../Report/new/ReportButton.js/ReportButton";
import SharedInformation from "../SharedInformation/SharedInformation";
import { IconButton, Tooltip } from "../UI";
import ConopsInformation from "./ConopsInformation/ConopsInformation";
import { StyledDashboardPortal, StyledTabHeader } from "./Dashboard-styled";
import EditorManager from "./Editor/EditorManager";
import Reports from "./Editor/Reports";
import Insights from "./Insights/Insights";

const sharedInformationWidth = 350;
const insightsWidth = 470;

const WidgetToTab = {
  SharedInformation: "sharedInformation",
  Insights: "insights",
  LatestUpdates: "latestUpdates",
  Editor: "editor",
};
const Dashboard = () => {
  const [show, setShow] = useState(false);
  const { config } = useContext(ConfigContext);
  const [_dashboardCount, setDashboardCount] = useState(0);
  const [selectedTab, setSelectedTab] = useState("");
  const [showConops, setShowConops] = useState(false);
  const { t } = useTranslation("common");
  const sidebarRef = useRef();
  const dashboardTabs = useRef(new Set());
  const dashboardCount = useRef(0);
  const userSelectedTab = useRef("");

  const layersLoading = useSelector((state) => state.layersLoading);
  const activeModule = useSelector((state) => state.activeModule);
  const showLatestUpdates = useSelector((state) => state.showLatestUpdates);
  const featureTableReducer = useSelector((state) => state.featureTableReducer);
  const dashboard = useSelector((state) => state.dashboard);
  const printOpen = useSelector((state) => state.printWidget.open);
  const printLoading = useSelector((state) => state.printWidget.isLoading);
  const isOnboardingActive = useSelector(
    (state) => state.onboardingReducer.active
  );
  const [editingActive, setEditingActive] = useState(false);

  const { editUpdateType, reportedFeature, showReportsPanel } = dashboard;

  const visibleLayers = useRef({
    shared: {},
    insights: {},
    latestUpdates: {},
    conops: {},
  });

  const isStockPrepositionLayer = useMemo(() => {
    return (
      view.map.layers.filter((l) => {
        if (visibleLayers.current.insights[l.id]) {
          const layerConfigName =
            l.layerConfig?.extends || l.layerConfig?.alias;
          return layerConfigName.includes("stockpreposition");
        }
        return false;
      }).length > 0
    );
  }, [_dashboardCount]);

  const getDefaultWidth = () => {
    switch (selectedTab) {
      case "insights":
        return isStockPrepositionLayer ? window.innerWidth / 2 : insightsWidth;
      default:
        return sharedInformationWidth;
    }
  };

  const {
    expand: isExpanded,
    setExpand,
    initDrag,
    onArrowClick,
    isMobile,
    mobileOffset,
    expandToWidth,
  } = useDragSidebar({
    defaultWidth: getDefaultWidth(),
    ref: sidebarRef,
    show: dashboardTabs.current.size > 0,
  });

  useReports(config);

  const expand = useMemo(
    () => isExpanded || isOnboardingActive,
    [isExpanded, isOnboardingActive]
  );

  useEffect(() => {
    if (featureTableReducer.open) {
      setExpand(false);
    } else if (dashboardTabs.current.size > 0 && !!selectedTab) {
      setExpand(true);
    }
  }, [featureTableReducer.open]);

  // for editor
  const dispatch = useDispatch();
  useEffect(() => {
    let handler;

    if (editUpdateType) return;

    if (
      config.role === ROLE_EDITOR &&
      (config.newEditorEnabled || config.cycleUpdateFrequency)
    ) {
      /** Push report an update button on the popup */
      handler = reactiveUtils.on(
        () => view.popup,
        "trigger-action",
        (event) => {
          if (event.action.id === "edit-action") {
            setSelectedTab("editor");
            userSelectedTab.current = "editor";

            const feature = view.popup.selectedFeature;
            dispatch(setEditableFeature(feature));
            setTimeout(() => {
              setExpand(true);
            }, 100);
          }
        }
      );
    }

    return () => {
      handler?.remove();
    };
  }, [editUpdateType, featureTableReducer.open]);

  useEffect(() => {
    if (!isMobile) return;
    const viewDiv = document.querySelector("#viewDiv");
    if (show) {
      viewDiv.style.height = `calc(100% - ${mobileOffset}px)`;
    } else {
      viewDiv.style.height = ``;
      if (sidebarRef.current) {
        sidebarRef.current.style.flexBasis = "";
      }
    }
  }, [show]);

  const handleShowSidebar = useCallback(() => {
    setShow(true);
  }, []);

  const handleCloseSidebar = useCallback(() => {
    setShow(false);
  }, []);

  useEffect(() => {
    userSelectedTab.current = "";
    setSelectedTab("");
  }, []);

  const processTab = ({ tab, visible }) => {
    if (visible) {
      dashboardTabs.current.add(tab);
      if (!isMobile && !featureTableReducer.open) {
        setExpand(true);
      }
    } else {
      dashboardTabs.current.delete(tab);
    }
    dashboardCount.current += 1;
    setDashboardCount(dashboardCount.current);
  };

  const handleInsightsLayer = (layer, visible) => {
    const insights = config.insights || {};
    // const insightsLayer = config.role === ROLE_EDITOR ? insights.editorLayer : insights.publicLayer;
    const hasInsightsLayer =
      config.role === ROLE_EDITOR
        ? layer.layerConfig?.isShownInEditorInsights
        : layer.layerConfig?.isShownInPublicInsights;

    if (!hasInsightsLayer || !isWidgetDisplayed(config, "Insights")) return;

    const tab = "insights";
    visibleLayers.current.insights[layer.id] = visible;
    const arrayVisibleLayers = Object.values(
      visibleLayers.current.insights
    ).filter((visible) => visible);
    processTab({ tab, visible: arrayVisibleLayers.length > 0 });
  };

  const handleConopsLayer = (layer, visible) => {
    if (layer.layerConfig?.isConops) {
      const tab = "conops";
      visibleLayers.current.conops[layer.id] = visible;
      const arrayVisibleLayers = Object.values(
        visibleLayers.current.conops
      ).filter((visible) => visible);

      setShowConops(arrayVisibleLayers.length > 0);

      if (arrayVisibleLayers.length === 0) {
        processTab({ tab, visible: false });
      }
    }
  };

  const watchHandlers = useRef([]);

  useEffect(() => {
    userSelectedTab.current = "";
    setSelectedTab("");
    dashboardCount.current++;
    setDashboardCount(dashboardCount.current);
    dashboardCount.current = 0;
    //for editor
    if (
      config.role === ROLE_EDITOR &&
      (config.newEditorEnabled || config.cycleUpdateFrequency)
    ) {
      requestAnimationFrame(() => {
        dashboardTabs.current.add("editor");
        setExpand(true);
        setSelectedTab("editor");
        dashboardCount.current++;
        setDashboardCount(dashboardCount.current);
      });
    }
  }, [activeModule]);

  let expandTimer;
  useEffect(() => {
    clearTimeout(expandTimer);
    expandTimer = setTimeout(() => {
      if (dashboardTabs.current.size === 0) {
        setExpand(false);
      }
    }, 250);
  }, [_dashboardCount]);

  const processLayer = (layer, visible) => {
    if (!layer.layerConfig) return;
    handleInsightsLayer(layer, visible);

    handleConopsLayer(layer, visible);

    if (
      layer.layerConfig?.isShownInReportManager &&
      isWidgetDisplayed(config, "ReportManager")
    ) {
      const tab = "sharedInformation";
      visibleLayers.current.shared[layer.id] = visible;
      const arrayVisibleLayers = Object.values(
        visibleLayers.current.shared
      ).filter((visible) => visible);
      //remove for editor
      // if (
      //   !(
      //     config.role === ROLE_EDITOR &&
      //     (config.newEditorEnabled || config.cycleUpdateFrequency)
      //   )
      // ) {
      // processTab({ tab, visible: arrayVisibleLayers.length > 0 });
      //   }
    }
    // console.log(
    //   layer.layerConfig?.isShownInLatestUpdates,
    //   isWidgetDisplayed(config, "LatestUpdates")
    // );

    if (
      layer.layerConfig?.isShownInLatestUpdates &&
      isWidgetDisplayed(config, "LatestUpdates")
    ) {
      visibleLayers.current.latestUpdates[layer.id] = visible;
      const arrayVisibleLayers = Object.values(
        visibleLayers.current.latestUpdates
      ).filter((visible) => visible);
      const tab = "latestUpdates";
      processTab({ tab, visible: arrayVisibleLayers.length > 0 });
    }
  };

  useEffect(() => {
    if (watchHandlers.current.length > 0) {
      watchHandlers.current.forEach((h) => h.remove());
      watchHandlers.current = [];
    }

    const layersChangeHandler = view.map.layers.on("change", (event) => {
      if (event.added)
        event.added.forEach((layer) => {
          if (!layer.layerConfig) return;
          processLayer(layer, layer.visible);
          const visibleHandler = reactiveUtils.watch(
            () => layer.visible,
            (visible) => {
              processLayer(layer, visible);
            }
          );
          watchHandlers.current.push(visibleHandler);
        });
    });
    watchHandlers.current.push(layersChangeHandler);

    view.map.layers.forEach((layer) => {
      processLayer(layer, layer.visible);
      const visibleHandler = reactiveUtils.watch(
        () => layer.visible,
        (visible) => {
          processLayer(layer, visible);
        }
      );
      watchHandlers.current.push(visibleHandler);
    });

    return () => {
      watchHandlers.current.forEach((h) => h.remove());
    };
  }, [layersLoading, featureTableReducer.open]);

  let timer;
  useEffect(() => {
    clearTimeout(timer);
    setTimeout(() => {
      if (
        (!userSelectedTab.current ||
          (userSelectedTab.current &&
            !dashboardTabs.current.has(userSelectedTab.current))) &&
        dashboardTabs.current.size > 0
      ) {
        const moduleOpenWidgets = config.modules
          ? config.modules[activeModule]?.openWidgets ?? []
          : [];
        const openWidget =
          moduleOpenWidgets.length > 0 ? moduleOpenWidgets[0] : null;

        if (config.role !== ROLE_EDITOR) {
          if (
            openWidget &&
            dashboardTabs.current.has(WidgetToTab[openWidget])
          ) {
            setSelectedTab(WidgetToTab[openWidget]);
          } else {
            if (dashboardTabs.current.has("sharedInformation")) {
              setSelectedTab("sharedInformation");
            } else if (dashboardTabs.current.has("latestUpdates")) {
              setSelectedTab("latestUpdates");
            } else if (dashboardTabs.current.has("insights")) {
              setSelectedTab("insights");
            } else if (dashboardTabs.current.has("editor")) {
              setSelectedTab("editor");
            } else if (dashboardTabs.current.has("conops")) {
              setSelectedTab("conops");
            }
          }
        }
      }
    }, 250);
    return () => {
      clearTimeout(timer);
    };
  }, [_dashboardCount]);

  const handleTabSelection = useCallback(
    (tab) => {
      if (!expand) {
        onArrowClick();
      }
      setSelectedTab(tab);
      userSelectedTab.current = tab;
    },
    [onArrowClick, expand]
  );

  const tabs = Array.from(dashboardTabs.current);

  const renderTab = () => {
    switch (selectedTab) {
      case "latestUpdates": {
        return <LatestUpdates expanded={expand} />;
      }
      case "insights": {
        return (
          <Insights
            expand={expand}
            showSidebar={handleShowSidebar}
            closeSidebar={handleCloseSidebar}
          />
        );
      }
      case "sharedInformation": {
        return <SharedInformation expand={expand} selectedTab={selectedTab} />;
      }
      case "editor": {
        return (
          <EditorManager expand={expand} setEditingActive={setEditingActive} />
        );
      }

      default: {
        return null;
      }
    }
  };

  //for editor
  const isEditor = useMemo(() => {
    return (
      config.role === ROLE_EDITOR &&
      (config.newEditorEnabled || config.cycleUpdateFrequency)
    );
  }, [config]);

  const opsColor = useMemo(() => {
    return getOpsColor(config, activeModule);
  }, [config, activeModule]);

  useEffect(() => {
    const checkHeight = () => {
      requestAnimationFrame(() => {
        if (sidebarRef.current) {
          if (sidebarRef.current.offsetHeight < 150) {
            sidebarRef.current.classList.add("hidden");
          } else {
            sidebarRef.current.classList.remove("hidden");
          }
        }
      });
    };

    const resizeObserver = new ResizeObserver(checkHeight);
    if (sidebarRef.current) {
      resizeObserver.observe(sidebarRef.current);
    }

    return () => {
      if (sidebarRef.current) {
        resizeObserver.unobserve(sidebarRef.current);
      }
    };
  }, [_dashboardCount]);

  const hasReport = useMemo(() => {
    return isWidgetDisplayed(config, "Report");
  }, [config]);

  if (printOpen && !printLoading) return null;

  if (config.role !== ROLE_EDITOR) {
    return (
      <ViewerSidebar
        ref={sidebarRef}
        show={tabs.length > 0}
        expand={isOnboardingActive || expand}
        newEditorEnabled={
          config.role === ROLE_EDITOR &&
          (config.newEditorEnabled || config.cycleUpdateFrequency)
        }
        defaultWidth={
          tabs.includes("insights")
            ? isStockPrepositionLayer
              ? window.innerWidth / 2
              : insightsWidth
            : sharedInformationWidth
        }
        className="right-sidebar"
      >
        <StyledResizerWrapper>
          <StyledResizerButton onClick={onArrowClick}>
            <SectionItem
              style={{
                borderRadius: "10px 0px 0px 10px",
                height: "100%",
                width: "30px",
                boxSizing: "border-box",
                padding: "0px",
              }}
            >
              <FaChevronRight
                style={{
                  transform: `rotate(${expand ? "0deg" : "-180deg"})`,
                  transition: "all 0.3s",
                }}
              />
            </SectionItem>
          </StyledResizerButton>
        </StyledResizerWrapper>
        <ViewerSidebarBody expand={expand}>
          <StyledTabHeader expand={expand}>
            {showLatestUpdates && tabs.includes("latestUpdates") && (
              <Tooltip
                showOnHover
                content={t("screen.widget.LatestUpdates.title")}
                blurred
                placement="top"
              >
                <IconButton
                  onClick={() => {
                    handleTabSelection("latestUpdates");
                  }}
                  selected={selectedTab === "latestUpdates"}
                  className="onboarding-latest-updates"
                >
                  <LatestUpdatesIcon />
                </IconButton>
              </Tooltip>
            )}

            {tabs.includes("insights") && (
              <Tooltip
                showOnHover
                content={t("screen.widget.Insights.title")}
                blurred
                placement="top"
              >
                <IconButton
                  onClick={() => handleTabSelection("insights")}
                  selected={selectedTab === "insights"}
                  className="onboarding-insights"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="currentColor"
                  >
                    <path d="M6 9H3v5h3zm-1 4H4v-3h1zm6-12H8v13h3zm-1 12H9V2h1zm3-8v9h3V5zm2 7.999h-1V6h1zM2 15h14v1H1V1h1z" />
                  </svg>
                </IconButton>
              </Tooltip>
            )}

            {tabs.includes("conops") && showConops && (
              <Tooltip
                showOnHover
                content={t("screen.widget.Conops.title")}
                blurred
                placement="top"
              >
                <IconButton
                  onClick={() => handleTabSelection("conops")}
                  selected={selectedTab === "conops"}
                  className=""
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="25"
                    viewBox="0 0 24 25"
                    fill="none"
                  >
                    <path
                      d="M21.625 11.125H21.0521C20.8302 9.65773 20.2531 8.26693 19.371 7.07363L19.7782 6.66654C19.9059 6.53888 20.0072 6.38731 20.0764 6.22049C20.1455 6.05368 20.1811 5.87488 20.1811 5.69431C20.1811 5.51374 20.1456 5.33494 20.0765 5.16811C20.0074 5.00127 19.9061 4.84968 19.7785 4.72199C19.6508 4.59429 19.4992 4.49299 19.3324 4.42387C19.1656 4.35475 18.9868 4.31916 18.8062 4.31914C18.6257 4.31912 18.4469 4.35467 18.28 4.42375C18.1132 4.49283 17.9616 4.5941 17.8339 4.72176L17.4264 5.12904C16.2331 4.24692 14.8423 3.66982 13.375 3.44794V2.875C13.375 2.51033 13.2301 2.16059 12.9723 1.90273C12.7144 1.64487 12.3647 1.5 12 1.5C11.6353 1.5 11.2856 1.64487 11.0277 1.90273C10.7699 2.16059 10.625 2.51033 10.625 2.875V3.44794C9.15772 3.66982 7.76689 4.24693 6.57357 5.12904L6.16611 4.72176C6.03842 4.59409 5.88683 4.49283 5.71999 4.42375C5.55316 4.35466 5.37436 4.31912 5.19379 4.31914C4.82911 4.31918 4.47938 4.46408 4.22155 4.72198C3.96371 4.97987 3.81888 5.32963 3.81892 5.69431C3.81896 6.05898 3.96387 6.40871 4.22176 6.66654L4.62904 7.07363C3.74693 8.26693 3.16982 9.65773 2.94794 11.125H2.375C2.01033 11.125 1.66059 11.2699 1.40273 11.5277C1.14487 11.7856 1 12.1353 1 12.5C1 12.8647 1.14487 13.2144 1.40273 13.4723C1.66059 13.7301 2.01033 13.875 2.375 13.875H2.94794C3.16982 15.3423 3.74693 16.7331 4.62904 17.9264L4.22176 18.3335C3.96387 18.5913 3.81896 18.941 3.81892 19.3057C3.8189 19.4863 3.85444 19.6651 3.92352 19.8319C3.99261 19.9987 4.09387 20.1503 4.22154 20.278C4.3492 20.4057 4.50077 20.507 4.66759 20.5761C4.8344 20.6452 5.0132 20.6808 5.19377 20.6809C5.55845 20.6809 5.9082 20.5361 6.16609 20.2782L6.57355 19.871C7.76688 20.7531 9.15771 21.3302 10.625 21.5521V22.125C10.625 22.4897 10.7699 22.8394 11.0277 23.0973C11.2856 23.3551 11.6353 23.5 12 23.5C12.3647 23.5 12.7144 23.3551 12.9723 23.0973C13.2301 22.8394 13.375 22.4897 13.375 22.125V21.5521C14.8423 21.3302 16.2331 20.7531 17.4264 19.871L17.8339 20.2782C17.9616 20.4059 18.1132 20.5072 18.28 20.5763C18.4468 20.6453 18.6256 20.6809 18.8062 20.6809C18.9868 20.6808 19.1656 20.6452 19.3324 20.5761C19.4992 20.507 19.6508 20.4057 19.7784 20.278C19.9061 20.1503 20.0074 19.9987 20.0765 19.8319C20.1455 19.6651 20.1811 19.4863 20.1811 19.3057C20.181 19.1251 20.1455 18.9463 20.0763 18.7795C20.0072 18.6127 19.9059 18.4611 19.7782 18.3335L19.3709 17.9264C20.2531 16.7331 20.8302 15.3423 21.0521 13.875H21.625C21.9897 13.875 22.3394 13.7301 22.5973 13.4723C22.8551 13.2144 23 12.8647 23 12.5C23 12.1353 22.8551 11.7856 22.5973 11.5277C22.3394 11.2699 21.9897 11.125 21.625 11.125ZM12 18.9167C10.7309 18.9167 9.49031 18.5403 8.43509 17.8353C7.37988 17.1302 6.55744 16.128 6.07177 14.9556C5.58611 13.7831 5.45904 12.4929 5.70663 11.2482C5.95422 10.0035 6.56534 8.86012 7.46273 7.96273C8.36012 7.06534 9.50346 6.45422 10.7482 6.20663C11.9929 5.95904 13.2831 6.08611 14.4556 6.57177C15.628 7.05744 16.6302 7.87988 17.3353 8.93509C18.0403 9.99031 18.4167 11.2309 18.4167 12.5C18.4148 14.2012 17.7381 15.8322 16.5352 17.0352C15.3322 18.2381 13.7012 18.9148 12 18.9167Z"
                      fill="currentColor"
                    />
                  </svg>
                </IconButton>
              </Tooltip>
            )}

            {hasReport && (
              <>
                <ReportButton config={config} t={t} />
              </>
            )}

            {createPortal(
              <div
                style={{
                  position: "fixed",
                  right: 16,
                  top: 16,
                  zIndex: 10,
                  borderRadius: "100px",
                  opacity: expand ? 0 : 1,
                  boxShadow: "rgba(0,0,0,0.16) 0px 1px 4px",
                }}
              >
                <ReportButton config={config} t={t} isOnMap />
              </div>,
              document.body
            )}
          </StyledTabHeader>

          <div
            style={{
              position: "relative",
              flex: 1,
            }}
          >
            {selectedTab === "latestUpdates" && (
              <LatestUpdates expanded={expand} />
            )}

            {selectedTab === "insights" && (
              <Insights
                expand={expand}
                showSidebar={handleShowSidebar}
                closeSidebar={handleCloseSidebar}
              />
            )}
            {showConops && (
              <ConopsInformation
                expand={expand}
                processTab={processTab}
                isSelected={selectedTab === "conops"}
              />
            )}
          </div>
        </ViewerSidebarBody>
      </ViewerSidebar>
    );
  }

  return (
    <ViewerSidebar
      ref={sidebarRef}
      show={tabs.length > 0}
      expand={expand}
      newEditorEnabled={
        config.role === ROLE_EDITOR &&
        (config.newEditorEnabled || config.cycleUpdateFrequency)
      }
      defaultWidth={
        tabs.includes("insights")
          ? isStockPrepositionLayer
            ? window.innerWidth / 2
            : insightsWidth
          : sharedInformationWidth
      }
      className="right-sidebar"
    >
      {showReportsPanel && reportedFeature && reportedFeature.getObjectId() && (
        <Reports config={config} t={t} />
      )}
      <StyledDashboardPortal
        style={{ position: "absolute", right: 0 }}
        id="dashboard-portal"
      />
      <StyledResizerWrapper>
        <StyledResizerButton onClick={onArrowClick}>
          <SectionItem
            style={{
              borderRadius: "8px 0px 0px 8px",
              height: "100%",
              width: "30px",
              boxSizing: "border-box",
              padding: "0px",
            }}
          >
            <FaChevronRight
              style={{
                transform: `rotate(${expand ? "0deg" : "-180deg"})`,
                transition: "all 0.3s",
              }}
            />
          </SectionItem>
        </StyledResizerButton>
      </StyledResizerWrapper>

      <StyledResizer onMouseDown={initDrag} expand={expand} />
      <ViewerSidebarBody
        style={{
          overflow: "hidden",
          paddingBottom: 8,
        }}
        expand={expand}
      >
        {!editingActive && (
          <StyledTabHeader expand={expand}>
            {showLatestUpdates && tabs.includes("latestUpdates") && (
              <Tooltip
                showOnHover
                content={t("screen.widget.LatestUpdates.title")}
                blurred
                placement="top"
              >
                <IconButton
                  onClick={() => {
                    handleTabSelection("latestUpdates");
                  }}
                  selected={selectedTab === "latestUpdates"}
                  className="onboarding-latest-updates"
                >
                  <LatestUpdatesIcon />
                </IconButton>
              </Tooltip>
            )}

            {tabs.includes("conops") && showConops && (
              <Tooltip
                showOnHover
                content={t("screen.widget.Conops.title")}
                blurred
                placement="top"
              >
                <IconButton
                  onClick={() => handleTabSelection("conops")}
                  selected={selectedTab === "conops"}
                  className=""
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="25"
                    viewBox="0 0 24 25"
                    fill="none"
                  >
                    <path
                      d="M21.625 11.125H21.0521C20.8302 9.65773 20.2531 8.26693 19.371 7.07363L19.7782 6.66654C19.9059 6.53888 20.0072 6.38731 20.0764 6.22049C20.1455 6.05368 20.1811 5.87488 20.1811 5.69431C20.1811 5.51374 20.1456 5.33494 20.0765 5.16811C20.0074 5.00127 19.9061 4.84968 19.7785 4.72199C19.6508 4.59429 19.4992 4.49299 19.3324 4.42387C19.1656 4.35475 18.9868 4.31916 18.8062 4.31914C18.6257 4.31912 18.4469 4.35467 18.28 4.42375C18.1132 4.49283 17.9616 4.5941 17.8339 4.72176L17.4264 5.12904C16.2331 4.24692 14.8423 3.66982 13.375 3.44794V2.875C13.375 2.51033 13.2301 2.16059 12.9723 1.90273C12.7144 1.64487 12.3647 1.5 12 1.5C11.6353 1.5 11.2856 1.64487 11.0277 1.90273C10.7699 2.16059 10.625 2.51033 10.625 2.875V3.44794C9.15772 3.66982 7.76689 4.24693 6.57357 5.12904L6.16611 4.72176C6.03842 4.59409 5.88683 4.49283 5.71999 4.42375C5.55316 4.35466 5.37436 4.31912 5.19379 4.31914C4.82911 4.31918 4.47938 4.46408 4.22155 4.72198C3.96371 4.97987 3.81888 5.32963 3.81892 5.69431C3.81896 6.05898 3.96387 6.40871 4.22176 6.66654L4.62904 7.07363C3.74693 8.26693 3.16982 9.65773 2.94794 11.125H2.375C2.01033 11.125 1.66059 11.2699 1.40273 11.5277C1.14487 11.7856 1 12.1353 1 12.5C1 12.8647 1.14487 13.2144 1.40273 13.4723C1.66059 13.7301 2.01033 13.875 2.375 13.875H2.94794C3.16982 15.3423 3.74693 16.7331 4.62904 17.9264L4.22176 18.3335C3.96387 18.5913 3.81896 18.941 3.81892 19.3057C3.8189 19.4863 3.85444 19.6651 3.92352 19.8319C3.99261 19.9987 4.09387 20.1503 4.22154 20.278C4.3492 20.4057 4.50077 20.507 4.66759 20.5761C4.8344 20.6452 5.0132 20.6808 5.19377 20.6809C5.55845 20.6809 5.9082 20.5361 6.16609 20.2782L6.57355 19.871C7.76688 20.7531 9.15771 21.3302 10.625 21.5521V22.125C10.625 22.4897 10.7699 22.8394 11.0277 23.0973C11.2856 23.3551 11.6353 23.5 12 23.5C12.3647 23.5 12.7144 23.3551 12.9723 23.0973C13.2301 22.8394 13.375 22.4897 13.375 22.125V21.5521C14.8423 21.3302 16.2331 20.7531 17.4264 19.871L17.8339 20.2782C17.9616 20.4059 18.1132 20.5072 18.28 20.5763C18.4468 20.6453 18.6256 20.6809 18.8062 20.6809C18.9868 20.6808 19.1656 20.6452 19.3324 20.5761C19.4992 20.507 19.6508 20.4057 19.7784 20.278C19.9061 20.1503 20.0074 19.9987 20.0765 19.8319C20.1455 19.6651 20.1811 19.4863 20.1811 19.3057C20.181 19.1251 20.1455 18.9463 20.0763 18.7795C20.0072 18.6127 19.9059 18.4611 19.7782 18.3335L19.3709 17.9264C20.2531 16.7331 20.8302 15.3423 21.0521 13.875H21.625C21.9897 13.875 22.3394 13.7301 22.5973 13.4723C22.8551 13.2144 23 12.8647 23 12.5C23 12.1353 22.8551 11.7856 22.5973 11.5277C22.3394 11.2699 21.9897 11.125 21.625 11.125ZM12 18.9167C10.7309 18.9167 9.49031 18.5403 8.43509 17.8353C7.37988 17.1302 6.55744 16.128 6.07177 14.9556C5.58611 13.7831 5.45904 12.4929 5.70663 11.2482C5.95422 10.0035 6.56534 8.86012 7.46273 7.96273C8.36012 7.06534 9.50346 6.45422 10.7482 6.20663C11.9929 5.95904 13.2831 6.08611 14.4556 6.57177C15.628 7.05744 16.6302 7.87988 17.3353 8.93509C18.0403 9.99031 18.4167 11.2309 18.4167 12.5C18.4148 14.2012 17.7381 15.8322 16.5352 17.0352C15.3322 18.2381 13.7012 18.9148 12 18.9167Z"
                      fill="currentColor"
                    />
                  </svg>
                </IconButton>
              </Tooltip>
            )}

            {tabs.includes("editor") && (
              <Tooltip
                showOnHover
                content={t("screen.widget.Editor.manager.title")}
                blurred
                placement="top"
              >
                <IconButton
                  onClick={() => handleTabSelection("editor")}
                  selected={selectedTab === "editor"}
                  className=""
                >
                  <svg
                    stroke="currentColor"
                    fill="currentColor"
                    strokeWidth="0"
                    viewBox="0 0 24 24"
                    height="20px"
                    width="20px"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path d="M17.263 2.177a1.75 1.75 0 0 1 2.474 0l2.586 2.586a1.75 1.75 0 0 1 0 2.474L19.53 10.03l-.012.013L8.69 20.378a1.753 1.753 0 0 1-.699.409l-5.523 1.68a.748.748 0 0 1-.747-.188.748.748 0 0 1-.188-.747l1.673-5.5a1.75 1.75 0 0 1 .466-.756L14.476 4.963ZM4.708 16.361a.26.26 0 0 0-.067.108l-1.264 4.154 4.177-1.271a.253.253 0 0 0 .1-.059l10.273-9.806-2.94-2.939-10.279 9.813ZM19 8.44l2.263-2.262a.25.25 0 0 0 0-.354l-2.586-2.586a.25.25 0 0 0-.354 0L16.061 5.5Z" />
                  </svg>
                </IconButton>
              </Tooltip>
            )}
          </StyledTabHeader>
        )}
        {selectedTab === "" ? (
          <div
            style={{
              position: "absolute",
              width: "100%",
              height: `calc(100% - ${isMobile ? "34px" : "44px"})`,
              top: isMobile ? "34px" : "44px",
            }}
          ></div>
        ) : (
          <div style={{ position: "relative", height: "100%" }}>
            {renderTab()}
            {showConops && (
              <ConopsInformation
                expand={expand}
                processTab={processTab}
                isSelected={selectedTab === "conops"}
              />
            )}
          </div>
        )}
      </ViewerSidebarBody>
      {/*</div>*/}
    </ViewerSidebar>
  );
};

export default Dashboard;
